export const SCHEDULED_GEOMETRY_VERSION = 4

export type ScheduledGeometrySlices = Record<string, Array<[number, number]>>

export interface ScheduledGeometryManifest {
  version: typeof SCHEDULED_GEOMETRY_VERSION
  feedVersion: string
  sourceFeedVersions: string[]
  dates: string[]
  geometryIdentities: string[]
  shards: Array<string | null>
  retainedShards?: string[]
}

interface RequestEntry<T> {
  promise: Promise<T>
  signal: AbortSignal
  settled: boolean
}

let manifestRequest: RequestEntry<ScheduledGeometryManifest> | null = null
const shardRequests = new Map<string, RequestEntry<ScheduledGeometrySlices>>()

export async function scheduledGeometry(
  identity: string,
  from: number,
  to: number,
  context: { feedVersion: string; date: string },
  signal: AbortSignal,
): Promise<Array<[number, number]> | null> {
  if (!manifestRequest || (!manifestRequest.settled && manifestRequest.signal.aborted)) {
    const request: RequestEntry<ScheduledGeometryManifest> = { promise: null!, signal, settled: false }
    request.promise = fetch('/shapes/manifest.json', { signal }).then(async (response) => {
      if (!response.ok) throw new Error(`Scheduled geometry manifest returned ${response.status}.`)
      const value = await response.json() as ScheduledGeometryManifest
      if (value.version !== SCHEDULED_GEOMETRY_VERSION || !Array.isArray(value.shards)) {
        throw new Error('Scheduled geometry is incompatible.')
      }
      return value
    }).catch((cause) => {
      if (manifestRequest === request) manifestRequest = null
      throw cause
    }).finally(() => { request.settled = true })
    manifestRequest = request
  }
  const manifest = await manifestRequest.promise
  if ((context.feedVersion !== manifest.feedVersion && !manifest.sourceFeedVersions.includes(context.feedVersion))
    || !manifest.dates.includes(context.date)
    || !identity || !Number.isInteger(from) || !Number.isInteger(to) || to <= from) return null
  const identityIndex = manifest.geometryIdentities.indexOf(identity)
  const shardUrl = identityIndex < 0 ? null : manifest.shards[identityIndex]
  if (!shardUrl) return null
  let shardRequest = shardRequests.get(shardUrl)
  if (!shardRequest || (!shardRequest.settled && shardRequest.signal.aborted)) {
    const request: RequestEntry<ScheduledGeometrySlices> = { promise: null!, signal, settled: false }
    request.promise = fetch(shardUrl, { signal }).then(async (response) => {
      if (!response.ok) throw new Error(`Scheduled geometry shard returned ${response.status}.`)
      const value = await response.json() as ScheduledGeometrySlices
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error('Scheduled geometry shard is incompatible.')
      }
      return value
    }).catch((cause) => {
      if (shardRequests.get(shardUrl) === request) shardRequests.delete(shardUrl)
      throw cause
    }).finally(() => { request.settled = true })
    shardRequest = request
    shardRequests.set(shardUrl, request)
  }
  const slices = await shardRequest.promise
  const direct = slices[`${from}:${to}`]
  if (direct) return direct
  const combined: Array<[number, number]> = []
  for (let index = from; index < to; index += 1) {
    const slice = slices[`${index}:${index + 1}`]
    if (!slice) return null
    combined.push(...(combined.length ? slice.slice(1) : slice))
  }
  return combined
}
